import React, { useEffect, useState, useCallback, useMemo } from 'react'
import Slider from 'react-slick'
import { Typography, Box, useTheme, Skeleton } from '@mui/material'
import {
  IMAGE_BASE_PATH_DBRAIMT,
  ORGANIZATION_ID,
  SUB_ORGANIZATION_ID,
} from '../../Config/Config'
import { fetchData, getWebsiteId } from '../../Assets/API/apiservices'

// React.memo to prevent unnecessary re-renders
const Header_Carousel = React.memo(({ pageName = 'home' }) => {
  const [bannersData, setBannersData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadedImages, setLoadedImages] = useState({})
  const theme = useTheme()

  // Slider settings
  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      autoplay: true,
      autoplaySpeed: 3000,
      lazyLoad: 'ondemand',
    }),
    []
  )

  // Image style generator
  const getImageStyles = useCallback(
    () => ({
      width: '100%',
      height: 'auto',
      aspectRatio: '16/6',
      objectFit: 'cover',
      transition: 'filter 0.5s ease-out, opacity 0.5s ease-out',
      opacity: 0,
    }),
    []
  )

  // Mark image as loaded
  const handleImageLoad = useCallback((id) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }))
  }, [])

  // Fetch data when pageName changes
  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true)
        const website_id = getWebsiteId()
        if (!website_id) throw new Error('Website ID not available')

        const pagesRes = await fetchData(
          `websitepages?website_id=${website_id}&organization_id=${ORGANIZATION_ID}&sub_organization_id=${SUB_ORGANIZATION_ID}&type=list`
        )

        const page = pagesRes.rows?.find((p) =>
          p.name?.toLowerCase().includes(pageName.toLowerCase())
        )
        if (!page) throw new Error(`No page found for ${pageName}`)
        const website_page_id = page.id

        const bannersRes = await fetchData(
          `banners?organization_id=${ORGANIZATION_ID}&sub_organization_id=${SUB_ORGANIZATION_ID}&website_id=${website_id}&website_page_id=${website_page_id}`
        )

        const apiBanners = bannersRes.rows.map((row) => ({
          id: row.id,
          src: IMAGE_BASE_PATH_DBRAIMT + row.image_url,
        }))

        setBannersData(apiBanners)
      } catch (err) {
        console.error('Error fetching banners:', err.message)
      } finally {
        setIsLoading(false)
      }
    }

    getData()
  }, [pageName])

  // Loading skeleton
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        sx={{
          minHeight: { xs: '30vh', md: '50vh' },
          bgcolor: theme.palette.background.default,
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="50vh"
          animation="wave"
        />
      </Box>
    )
  }

  // Empty state
  if (!bannersData.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        sx={{
          minHeight: { xs: '40vh', md: '60vh' },
          bgcolor: theme.palette.background.default,
        }}
      >
        <Typography variant="h5" color="textSecondary" textAlign="center">
          🎉 Welcome! Stay tuned for exciting updates! 🎉
        </Typography>
      </Box>
    )
  }

  return (
    <Slider {...settings}>
      {bannersData.map((banner) => (
        <div key={banner.id} style={{ width: '100%', textAlign: 'center' }}>
          <img
            src={banner.src}
            style={{
              ...getImageStyles(),
              filter: loadedImages[banner.id] ? 'none' : 'blur(10px)',
              opacity: loadedImages[banner.id] ? 1 : 0,
            }}
            alt={`banner-${banner.id}`}
            loading="lazy"
            onLoad={() => handleImageLoad(banner.id)}
            onError={(e) => {
              e.target.onerror = null
              e.target.src = `${IMAGE_BASE_PATH_DBRAIMT}placeholder.jpg`
            }}
          />
        </div>
      ))}
    </Slider>
  )
})

export default Header_Carousel
